// RADIAL_USED: llm/skills/button/SKILL.md | COMPONENTS: TextButton | HOOKS: none
import { TextButton } from "@cdkglobal/radial";
import { IOption, NavItem, SaveUserFavoritesRequest } from "../../../../types";
import {
  CardRowStyled,
  DepartmentLabel,
  FavoriteCard,
  SectionTitle,
  SetupsCardRowWrapper,
  SetupTitle,
} from "../../../../utils/styled";
import { InfoCard } from "./SetupCards";
import { useMemo } from "react";
import { useUserContext } from "../../../../contexts/UserContext";
import { useMutation } from "@tanstack/react-query";
import { fetchFavoritesSetupList, saveFavorites } from "../../helpers";
import { useUIStateContext } from "../../../../contexts/UIStateContext";
import { ampli } from "../../../../ampli";

export function FavoritesSection({
  filteredFavItems,
  handleSetupClick,
}: Readonly<{
  filteredFavItems: NavItem[];
  handleSetupClick: ({
    setup,
    department,
  }: {
    setup: IOption;
    department: IOption;
  }) => void;
}>): JSX.Element {
  const {
    favorites,
    initialFavorites,
    setupBaseRequestInfo,
    setupListNavInfoByDepartment,
    setFavorites,
    setInitialFavorites,
    setIsFetchingUserData,
  } = useUserContext();
  const { setNetworkErrorToast } = useUIStateContext();

  const { mutate: saveFavoritesMutation, isPending: isSaving } = useMutation({
    mutationFn: (request: SaveUserFavoritesRequest) => saveFavorites(request),

    onSuccess: async (toastInfo) => {
      setNetworkErrorToast(toastInfo);
      if (toastInfo.variant === "positive") {
        setIsFetchingUserData(true);
        try {
          const { toastInfo: favoritesToastInfo, favoritesSetupInfoList } =
            await fetchFavoritesSetupList({
              setupListNavInfoByDepartment,
              setupBaseRequestInfo,
            });
          if (favoritesToastInfo) {
            setNetworkErrorToast(favoritesToastInfo);
            return;
          }
          setFavorites(favoritesSetupInfoList);
          setInitialFavorites(favoritesSetupInfoList);
        } catch (error) {
          console.error("Refresh favorites after save failed", error);
          setNetworkErrorToast({
            variant: "negative",
            message: "Unable to refresh favorites",
          });
        } finally {
          setIsFetchingUserData(false);
        }
      }
    },

    onError: (error) => {
      console.error("Save favorites failed", error);
      setNetworkErrorToast({
        variant: "negative",
        message: "Unable to save favorites",
      });
    },
  });

  const handleSaveFavorites = () => {
    const addedSetups = favorites
      .filter(
        (favorite) =>
          !initialFavorites.some((initialFavorite) => initialFavorite.id === favorite.id),
      )
      .map((favorite) => favorite.id);

    const removedSetups = initialFavorites
      .filter(
        (initialFavorite) =>
          !favorites.some((favorite) => favorite.id === initialFavorite.id),
      )
      .map((favorite) => favorite.id);

    ampli.saveFavorites({
      "Added Setups": addedSetups,
      "Removed Setups": removedSetups,
    });

    const request: SaveUserFavoritesRequest = {
      ...setupBaseRequestInfo,
      setupsList: favorites.map((fav) => fav.id),
    };

    saveFavoritesMutation(request);
  };

  const isFavoritesUpdated = useMemo(() => {
    if (favorites.length !== initialFavorites.length) {
      return true;
    }

    return !favorites.every((favorite) =>
      initialFavorites.some((init) => init.id === favorite.id),
    );
  }, [favorites, initialFavorites]);

  return (
    <>
      <CardRowStyled>
        <SectionTitle>Favorites ({filteredFavItems.length})</SectionTitle>
        {isFavoritesUpdated && (
          <TextButton
            onClick={handleSaveFavorites}
            text={isSaving ? "Saving..." : "Save Favorites"}
            dataTestId={`save-favorites-btn`}
            isDisabled={isSaving}
          />
        )}
      </CardRowStyled>
      {filteredFavItems.length ? (
        <SetupsCardRowWrapper>
          {filteredFavItems.map((setupItem) => (
            <FavoriteCard
              key={setupItem.id}
              dataTestId={`${setupItem.id}-favorite-card`}
              onClick={() => {
                ampli.openSetup({
                  "Setup Name": setupItem.text,
                  Department: setupItem.department?.label,
                  "Is Favorite": true,
                });
                setupItem.department &&
                  handleSetupClick({
                    setup: {
                      label: setupItem.text,
                      value: setupItem.id,
                    },
                    department: setupItem.department,
                  })
              }}
            >
              <SetupTitle>{setupItem.text}</SetupTitle>

              <DepartmentLabel>{setupItem.department?.label}</DepartmentLabel>
            </FavoriteCard>
          ))}
        </SetupsCardRowWrapper>
      ) : (
        <InfoCard infoText="Your favorites list is currently empty." />
      )}
    </>
  );
}
